import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DialogPortal, DialogOverlay } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Mail, Phone, Calendar, Shield, Edit2, Save, X, User } from 'lucide-react'
import React, { useState } from 'react'

// Custom DialogContent without built-in close button
const CustomDialogContent = React.forwardRef<
  React.HTMLAttributes<HTMLDivElement>,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-gray-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className
      )}
      {...props}
    >
      {children}
    </div>
  </DialogPortal>
))
CustomDialogContent.displayName = 'CustomDialogContent'

interface UserDetailProps {
  user?: {
    id: string
    name: string
    email: string
    role: string
    status?: 'active' | 'inactive'
    createdAt?: string
    phone?: string
  }
  onUpdate?: (_userId: string, _data: Record<string, unknown>) => Promise<void>
  onClose?: () => void
}

export const UserDetail: React.FC<UserDetailProps> = ({ user, onUpdate, onClose }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'customer',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async () => {
    if (!user || !onUpdate) return
    setIsSubmitting(true)
    try {
      await onUpdate(user.id, editForm)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update user:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setEditForm({
      full_name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 'customer',
    })
    setIsEditing(false)
  }

  const getRoleVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive'
      case 'customer':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const getStatusVariant = (status: 'active' | 'inactive') => {
    switch (status) {
      case 'active':
        return 'default'
      case 'inactive':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown'
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    } catch {
      return 'Unknown'
    }
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-muted-foreground">
          <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Select a user to view details</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with title and buttons */}
      <div className="flex items-center justify-between pb-4 border-b">
        <h2 className="text-lg font-semibold">User Details</h2>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSubmitting}
                className="bg-primary-600 hover:bg-primary-700"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(true)}
                className="text-primary-600 border-primary-200 hover:bg-primary-50"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Edit
              </Button>
              {onClose && (
                <Button size="sm" variant="ghost" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* User Profile Section */}
      <div className="flex items-center space-x-4">
        <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-xl font-semibold text-primary-600">
            {user.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1">
          {isEditing ? (
            <Input
              value={editForm.full_name}
              onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              className="text-lg font-semibold"
            />
          ) : (
            <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={getRoleVariant(user.role)} className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              {user.role}
            </Badge>
            {user.status && <Badge variant={getStatusVariant(user.status)}>{user.status}</Badge>}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Contact Information
        </h4>

        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Mail className="h-4 w-4 text-gray-400" />
            <div className="flex-1">
              {isEditing ? (
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-900">Email</p>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Phone className="h-4 w-4 text-gray-400" />
            <div className="flex-1">
              {isEditing ? (
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Phone number"
                />
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-900">Phone</p>
                  <p className="text-sm text-gray-600">{user.phone || 'Not provided'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role Management */}
      {isEditing && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Role Management
          </h4>
          <div className="flex items-center space-x-3">
            <Shield className="h-4 w-4 text-gray-400" />
            <div className="flex-1">
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              >
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Additional Information
        </h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Calendar className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Member Since</p>
              <p className="text-sm text-gray-600">{formatDate(user.createdAt)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Recent Activity
        </h4>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-500">No recent activity to display</p>
        </div>
      </div>
    </div>
  )
}
